// m01 — Tinch (memorial-frame)
//
// Design discipline: silence. Subtraction is the brand.
//
// What lives here (card-preview, 5:7):
//   • A museum-mounted portrait frame: outer hairline + inner mat + inner
//     hairline. No drop-shadow, no rounded corners, no gloss.
//   • The photo (when present) is rendered with a very faint warm sepia.
//     When absent, a clean MemorialSilhouette fills the mat — never the
//     word "FOTOSURAT".
//   • A single, almost-invisible paper-fibre noise behind the card,
//     rendered with feTurbulence at very low opacity. Adds depth without
//     decoration.
//   • Years as a typographic moment: Marcellus italic, true em-dash,
//     generous tracking, hairline rule beneath.
//   • One signature element — a single candle SVG in the lower-right —
//     carrying the emotional weight without saying a word.
//
// No animation in the card preview. The card is a still photograph.

import { MemorialSilhouette, Photo, type LayoutCtx } from "./_shared";

export function MemorialFrameLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Pull the years out of "1948 — 2026" if the parent supplies them, but the
  // card surface is a preview of the stable Tinch identity — birthYear is
  // not in LayoutCtx, so we defer the years moment to the variant. Here we
  // show the dignified date+venue caption only.

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        padding: "10%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {/* Faint paper-fibre texture, behind everything. Almost invisible. */}
      <svg
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0.06,
          pointerEvents: "none",
          mixBlendMode: "multiply",
        }}
      >
        <filter id="m01-card-paper">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            stitchTiles="stitch"
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.16  0 0 0 0 0.14  0 0 0 0 0.12  0 0 0 0.7 0"
          />
        </filter>
        <rect width="100%" height="100%" filter="url(#m01-card-paper)" />
      </svg>

      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.5em",
          color: `${ink}99`,
          marginBottom: "6%",
        }}
      >
        IN LOVING MEMORY
      </div>

      {/* Museum-mounted portrait: outer hairline + mat + inner hairline. */}
      <div
        style={{
          width: "55%",
          aspectRatio: "3/4",
          border: `1px solid ${ink}55`,
          padding: "4%",
          marginBottom: "6%",
          boxShadow: `inset 0 0 0 1px ${bg}, inset 0 0 0 2px ${ink}1A`,
          background: bg,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            background: `${ink}0E`,
            display: "grid",
            placeItems: "center",
            position: "relative",
            outline: `1px solid ${ink}33`,
            outlineOffset: "-1px",
          }}
        >
          {photo ? (
            <Photo
              src={photo}
              sepia
              style={{ position: "absolute", inset: 0 }}
            />
          ) : (
            <MemorialSilhouette tone={ink} accent={accent} bg={`${ink}08`} />
          )}
        </div>
      </div>

      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 22,
          lineHeight: 1.1,
          fontWeight: 300,
          letterSpacing: "0.01em",
        }}
      >
        {names}
      </div>

      <div
        style={{ height: 1, width: "30%", background: `${ink}33`, margin: "5% auto 4%" }}
      />

      <div
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 10,
          lineHeight: 1.6,
          color: `${ink}aa`,
          fontStyle: "italic",
          maxWidth: "80%",
        }}
      >
        Xotira marosimiga taklif qilamiz
      </div>

      <div style={{ flex: 1 }} />

      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.32em",
          color: `${ink}cc`,
        }}
      >
        {date} · {venue.toUpperCase()}
      </div>

      {/* Single candle — the signature element. Bottom-right, small, still. */}
      <svg
        aria-hidden
        viewBox="0 0 24 32"
        style={{
          position: "absolute",
          right: "8%",
          bottom: "7%",
          width: "5%",
          height: "auto",
          opacity: 0.55,
          pointerEvents: "none",
        }}
      >
        {/* Flame */}
        <path
          d="M12 2 C 13.6 5 15 7 15 9.5 C 15 11.6 13.6 13 12 13 C 10.4 13 9 11.6 9 9.5 C 9 7 10.4 5 12 2 Z"
          fill={accent}
          opacity="0.9"
        />
        <path
          d="M12 5 C 12.8 7 13.4 8.2 13.4 9.6 C 13.4 10.7 12.8 11.4 12 11.4 C 11.2 11.4 10.6 10.7 10.6 9.6 C 10.6 8.2 11.2 7 12 5 Z"
          fill={bg}
          opacity="0.55"
        />
        {/* Wick */}
        <line x1="12" y1="13" x2="12" y2="15" stroke={ink} strokeWidth="0.6" />
        {/* Candle body */}
        <rect x="9.5" y="15" width="5" height="14" fill={ink} opacity="0.85" />
        <rect x="9.5" y="15" width="5" height="1.2" fill={ink} opacity="0.5" />
        {/* Base */}
        <rect x="8" y="29" width="8" height="1.6" fill={ink} opacity="0.7" />
      </svg>
    </div>
  );
}
